// src/pages/bank-details/BankDetailsList.tsx
import { Column, DataTable } from '@/components/common/DataTable';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import useDebounce from '@/hooks/use-debounce';
import { getBankOptionLabel } from '@/lib/constants/bank-details';
import { DEFAULT_PAGINATION } from '@/lib/constants/common';
import { bankDetailsAPI } from '@/lib/utils/api';
import { BankDetails, BankDetailsResponse } from '@/types/bank-details.types';
import { Pagiantion } from '@/types/common.type';
import { Edit, Files, Info, Plus, Trash2 } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const BankDetailsList = () => {
  const [bankDetails, setBankDetails] = useState<BankDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('bankDetails_rowsPerPage');
    return saved ? Number(saved) : 10;
  });
  const [totalBankDetails, setTotalBankDetails] = useState<number>(0);

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    localStorage.setItem('bankDetails_rowsPerPage', String(rows));
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bankToDelete, setBankToDelete] = useState<BankDetails | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, rowsPerPage]);

  useEffect(() => {
    fetchBankDetails();
  }, [currentPage, debouncedSearch, rowsPerPage]);

  // Fetch all bank details
  const fetchBankDetails = async (): Promise<void> => {
    try {
      setLoading(true);

      const params: any = debouncedSearch ? { search: debouncedSearch } : {};
      params.page = currentPage;
      params.limit = rowsPerPage;
      const response = await bankDetailsAPI.getAll(params);
      const data = response.data as BankDetailsResponse | BankDetails[];
      const {
        total,
        limit = rowsPerPage,
        page,
        pages,
      }: Pagiantion = response.data?.pagination ?? DEFAULT_PAGINATION;

      setBankDetails(Array.isArray(data) ? data : (data.bankDetails ?? []));
      setTotalBankDetails(total);
    } catch {
      toast.error('Failed to fetch bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (bank: BankDetails) => {
    setBankToDelete(bank);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bankToDelete) return;

    try {
      setDeleting(true);
      await bankDetailsAPI.delete(bankToDelete._id);
      toast.success('Bank account deleted successfully');
      fetchBankDetails();
    } catch {
      toast.error('Failed to delete bank account');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setBankToDelete(null);
    }
  };

  // Duplicate bank account
  const handleDuplicate = async (bank: BankDetails): Promise<void> => {
    try {
      const duplicatedData: Omit<BankDetails, '_id'> & Partial<Pick<BankDetails, '_id'>> = {
        ...bank,
        bankName: bank.bankName,
        accountHolderName: `${bank.accountHolderName} (Copy)`,
        isDefault: false, // Duplicated account shouldn't be default
      };

      delete (duplicatedData as Partial<BankDetails>)._id;

      await bankDetailsAPI.create(duplicatedData);
      toast.success('Bank account duplicated successfully');
      fetchBankDetails();
    } catch {
      toast.error('Failed to duplicate bank account');
    }
  };

  // Mask account number for security
  const maskAccountNumber = (accountNumber: string): string => {
    if (!accountNumber || accountNumber.length < 4) return '****';
    return `****${accountNumber.slice(-4)}`;
  };

  const columns: Column<BankDetails>[] = [
    {
      key: 'bankDetails',
      header: 'Bank Details',
      cell: (bank) => (
        <>
          <div className="font-medium whitespace-nowrap w-fit min-w-fit">
            {bank.bankName ? getBankOptionLabel(bank.bankName) : '-'}
          </div>
          <div className="text-sm text-muted-foreground min-w-60 max-w-80 whitespace-break-spaces">
            {bank.accountHolderName}
          </div>
        </>
      ),
    },
    {
      key: 'accountNumber',
      header: 'Account Number',
      className: 'whitespace-nowrap w-fit min-w-fit',
      cell: (bank) => (
        <div className="font-mono text-sm">{maskAccountNumber(bank.accountNumber)}</div>
      ),
    },
    {
      key: 'ifscCode',
      header: 'IFSC Code',
      cell: (bank) => <div className="font-mono text-sm">{bank.ifscCode}</div>,
    },
    {
      key: 'upiId',
      header: 'UPI ID',
      cell: (bank) => <div className="text-sm">{bank.upiId || '-'}</div>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (bank) => (
        <Badge
          variant="secondary"
          className={`capitalize ${
            bank.isDefault ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {bank.isDefault ? 'Default' : 'Active'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right sticky right-0',
      cell: (bank) => (
        <div className="flex justify-end gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="text-blue-600 hover:bg-blue-50 hover:text-blue-600"
          >
            <Link to={`/bank-details/${bank._id}/edit`}>
              <Edit className="!h-[18px] !w-[18px]" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDuplicate(bank)}>
            <Files className="!h-[18px] !w-[18px]" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-600 hover:bg-red-50 hover:text-red-600"
            onClick={() => handleDeleteClick(bank)}
          >
            <Trash2 className="!h-[18px] !w-[18px]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <p className="text-muted-foreground">Manage your bank accounts and payment details</p>
        </div>
        <Button asChild>
          <Link to="/bank-details/new">
            <Plus className="mr-2 h-4 w-4" />
            Add Bank Account
          </Link>
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <Input
            type="search"
            placeholder="Search bank name, holder, IFSC, UPI or account no."
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className="max-w-96"
          />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 rounded-lg border p-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : bankDetails.length === 0 ? (
            <div className="py-12 text-center">
              <h3 className="mb-2 text-lg font-semibold">No bank accounts found</h3>
              <p className="mb-4 text-muted-foreground">
                {search
                  ? 'Try a different search term'
                  : 'Get started by adding your first bank account'}
              </p>
              <Button asChild>
                <Link to="/bank-details/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Bank Account
                </Link>
              </Button>
            </div>
          ) : (
            <DataTable
              data={bankDetails}
              columns={columns}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              total={totalBankDetails}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              emptyMessage="No bank accounts found"
            />
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent>
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <h4 className="mb-2 text-sm font-medium text-blue-900">Bank Account Information</h4>
              <div className="space-y-1 text-sm text-blue-800">
                <p>
                  <strong>Default Account:</strong> Only one bank account can be set as default at a
                  time.
                </p>
                <p>
                  <strong>Account Security:</strong> Full account numbers are masked for security.
                </p>
                <p>
                  <strong>UPI ID:</strong> Optional field for digital payment integration.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        entityName={
          bankToDelete?.bankName ? getBankOptionLabel(bankToDelete.bankName) : 'this bank account'
        }
        loading={deleting}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default BankDetailsList;
